import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { loadRazorpayScript } from "@/lib/razorpay";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, Flower2, HeartPulse, Info, Salad, Sparkles, Video } from "lucide-react";

const CARDS = [
  { key: "diet", title: "Diet · Ahara", icon: Salad },
  { key: "exercise", title: "Exercise & Yoga", icon: HeartPulse },
  { key: "reading", title: "Reading / Listening", icon: BookOpen },
  { key: "reflection", title: "Reflection & Practice", icon: Flower2 },
];
const TRI = { 1: "Trimester 1 · Months 1–3", 2: "Trimester 2 · Months 4–6", 3: "Trimester 3 · Months 7–9" };

const Story = ({ title, body, image, testid }) => (
  <div className="flex gap-5 items-start" data-testid={testid}>
    {image && <img src={mediaUrl(image)} alt={title} className="w-24 h-24 rounded-2xl object-cover border border-border shrink-0" />}
    <div>
      <h3 className="font-display text-2xl mb-2">{title}</h3>
      {body.split(/\n{2,}/).map((p, i) => (
        <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-2">{p}</p>
      ))}
    </div>
  </div>
);

export default function Garbhsanskar() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [phase, setPhase] = useState("preg");
  const [picked, setPicked] = useState({ pre: 1, preg: 1, post: 1 });
  const [joining, setJoining] = useState(false);
  const [startMonth, setStartMonth] = useState(1);

  useEffect(() => { api.get("/garbha/program").then(({ data }) => setData(data)).catch(() => {}); }, []);
  const loadMine = () => user && api.get("/garbha/me").then(({ data }) => setMine(data)).catch(() => {});
  useEffect(() => { loadMine(); }, [user]); // eslint-disable-line

  const join = async () => {
    if (!user) {
      toast.error("Please sign in to join the programme");
      return nav("/login?next=/garbhsanskar");
    }
    setJoining(true);
    try {
      const { data: s } = await api.post("/garbha/subscribe", { phase, month: startMonth });
      if (!(await loadRazorpayScript())) return toast.error("Razorpay could not load. Check your connection and try again.");
      const options = {
        key: s.key_id,
        name: "Immortal Vedas · Garbhsanskar",
        description: `₹${s.intro_amount} now, then ₹${s.monthly_amount}/month`,
        prefill: { name: user.name, email: user.email },
        theme: { color: "#215334" },
        handler: async (resp) => {
          try {
            await api.post("/garbha/activate", { phase, month: startMonth, razorpay_payment_id: resp.razorpay_payment_id });
            toast.success("Welcome to Garbhsanskar — your month is ready");
            loadMine();
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        },
      };
      if (s.subscription_id) options.subscription_id = s.subscription_id;
      else Object.assign(options, { order_id: s.order_id, amount: s.intro_amount * 100, currency: "INR" });
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed. Please try another method."));
      rzp.open();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setJoining(false); }
  };

  if (!data) return <div className="p-12 text-center font-display text-2xl">Loading Garbhsanskar…</div>;
  const m = data.meta;
  const enrolled = mine?.enrolled;

  return (
    <main data-testid="garbha-page">
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pt-12 pb-10 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
        <div>
          <div className="overline mb-3" data-testid="garbha-overline">{m.hero_overline}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="garbha-title">{m.hero_title}</h1>
          <p className="text-base lg:text-lg text-muted-foreground leading-relaxed mb-5">{m.hero_subtitle}</p>
          <div className="rounded-xl border border-secondary/40 bg-secondary/10 p-4 flex gap-3 mb-6" data-testid="garbha-disclaimer">
            <Info size={16} className="text-secondary mt-0.5 shrink-0" />
            <p className="text-xs leading-relaxed">{m.disclaimer}</p>
          </div>
          {enrolled ? (
            <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10" data-testid="garbha-enrolled-badge">
              Enrolled · {mine.participant.phase === "preg" ? "Pregnancy" : mine.participant.phase === "pre" ? "Pre-Pregnancy" : "Post-Pregnancy"} · Month {mine.participant.month}
            </Badge>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={startMonth}
                onChange={(e) => setStartMonth(parseInt(e.target.value, 10))}
                className="h-12 rounded-full border border-border bg-transparent px-4 text-sm"
                data-testid="garbha-start-month"
              >
                {(data.phases.find((p) => p.key === phase)?.stages || []).map((s) => (
                  <option key={s.key} value={s.month}>I am at {s.label}</option>
                ))}
              </select>
              <Button onClick={join} disabled={joining} className="btn-action h-12 px-7" data-testid="garbha-join">
                <Sparkles size={15} className="mr-2" /> {joining ? "Opening…" : m.hero_cta}
              </Button>
              <span className="text-xs text-muted-foreground">{m.price_note}</span>
            </div>
          )}
        </div>
        <img src={mediaUrl(m.hero_image)} alt={m.hero_title} className="rounded-[2rem] w-full aspect-[4/3] object-cover border border-border" data-testid="garbha-hero-image" />
      </section>

      {enrolled && (
        <section className="max-w-6xl mx-auto px-6 lg:px-12 pb-10 grid md:grid-cols-2 gap-4" data-testid="garbha-my-panel">
          <Card className="border-border"><CardContent className="p-5">
            <div className="overline mb-2">Your Samvad sessions</div>
            {(mine.upcoming_samvad || []).length === 0 && <p className="text-sm text-muted-foreground">No session scheduled yet.</p>}
            {(mine.upcoming_samvad || []).map((s) => (
              <div key={s.id} className="mb-3" data-testid={`garbha-samvad-${s.id}`}>
                <div className="font-medium text-sm">{s.title}</div>
                <div className="text-xs text-muted-foreground">{new Date(s.scheduled_at).toLocaleString()}{s.topic ? ` · ${s.topic}` : ""}</div>
                {s.session_link && (
                  <a href={s.session_link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1">
                    <Video size={11} /> Join the session
                  </a>
                )}
              </div>
            ))}
          </CardContent></Card>
          <Card className="border-border"><CardContent className="p-5">
            <div className="overline mb-2">Announcements</div>
            {(mine.announcements || []).length === 0 && <p className="text-sm text-muted-foreground">Nothing new from the clinic.</p>}
            {(mine.announcements || []).slice(0, 4).map((a) => (
              <div key={a.id} className="mb-3" data-testid={`garbha-announcement-${a.id}`}>
                <div className="font-medium text-sm">{a.title}</div>
                <p className="text-xs text-muted-foreground line-clamp-2">{a.body}</p>
              </div>
            ))}
          </CardContent></Card>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 lg:px-12 pb-16">
        <Tabs value={phase} onValueChange={(v) => { setPhase(v); setStartMonth(1); }}>
          <TabsList className="bg-muted flex-wrap h-auto">
            {data.phases.map((p) => (
              <TabsTrigger key={p.key} value={p.key} data-testid={`garbha-tab-${p.key}`}>
                {p.title} · {p.months} {p.months === 1 ? "month" : "months"}
              </TabsTrigger>
            ))}
          </TabsList>

          {data.phases.map((p) => (
            <TabsContent key={p.key} value={p.key} className="mt-7">
              <div className="mb-2 overline">{p.sanskrit}</div>
              <p className="text-sm text-muted-foreground mb-6">{p.note}</p>

              {p.key === "preg" && (
                <div className="flex gap-6 flex-wrap mb-3 text-[11px] text-muted-foreground">
                  {[1, 2, 3].map((t) => <span key={t}>{TRI[t]}</span>)}
                </div>
              )}
              <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-7" data-testid={`garbha-stepper-${p.key}`}>
                {p.stages.map((s, i) => {
                  const current = enrolled && mine.participant.stage_key === s.key;
                  const active = picked[p.key] === s.month;
                  return (
                    <React.Fragment key={s.key}>
                      {i > 0 && <span className={`h-px w-5 shrink-0 ${active ? "bg-primary" : "bg-border"}`} />}
                      <button
                        onClick={() => setPicked((v) => ({ ...v, [p.key]: s.month }))}
                        data-testid={`garbha-step-${s.key}`}
                        className={`shrink-0 rounded-full border px-4 py-2 text-xs transition-colors ${
                          active ? "bg-primary text-primary-foreground border-primary"
                                 : current ? "border-secondary text-secondary bg-secondary/10"
                                 : "border-border hover:border-primary/40"}`}
                      >
                        {s.label}{current ? " · you" : ""}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>

              {p.stages.filter((s) => s.month === picked[p.key]).map((s) => (
                <div key={s.key}>
                  {s.trimester > 0 && <Badge variant="outline" className="mb-4 text-[10px]">{TRI[s.trimester]}</Badge>}
                  <div className="grid sm:grid-cols-2 gap-5" data-testid={`garbha-cards-${s.key}`}>
                    {CARDS.map((c) => (
                      <Card key={c.key} className="border-border"><CardContent className="p-6">
                        <c.icon size={22} className="text-secondary mb-3" />
                        <h3 className="font-display text-xl mb-2">{c.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{s[c.key]}</p>
                      </CardContent></Card>
                    ))}
                  </div>
                  {s.image && <img src={mediaUrl(s.image)} alt={s.label} className="mt-5 rounded-2xl w-full max-h-72 object-cover border border-border" />}
                </div>
              ))}

              {p.key === "preg" && (
                <div className="mt-14 border-t border-border pt-10 space-y-10" data-testid="garbha-stories">
                  <div className="overline">Stories that shaped children before birth</div>
                  <Story title={m.story1_title} body={m.story1_body} image={m.story1_image} testid="garbha-story-1" />
                  <Story title={m.story2_title} body={m.story2_body} image={m.story2_image} testid="garbha-story-2" />
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </section>
    </main>
  );
}
